import React, { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import { z } from 'zod';
import TwilioSmsIcon from '@/assets/icons/twilio-sms.svg?react';
import Footer from '@/components/common/Footer';
import Header from '@/components/common/Header';
import useChannels from '@/hooks/useChannels';
import { useScrollbar } from '@/hooks/useScrollbar';
import {
  SmsContactFormError,
  SmsContactFormErrorCode,
  useSmsContactForm,
} from '@/hooks/useSmsContactForm';
import { useSmsFormStore } from '@/hooks/useSmsFormStore';
import { useWidgetUIStore } from '@/hooks/useWidgetUIStore';
import { ChannelOptions } from '@/types/widgetConfig';
import SmsSuccessPage from './SmsSuccessPage';

interface TwilioSmsChannelDetailProps {
  onMinimize?: () => void;
  onBack?: () => void;
}

const MAX_MESSAGE_LENGTH = 1028;
// Zod validation schema
const twilioSmsFormSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, 'twilioSms.validation.phoneRequired')
    .transform((phone) => (phone.startsWith('+') ? phone : `+${phone}`))
    .refine((phone) => isValidPhoneNumber(phone), {
      error: 'twilioSms.validation.phoneInvalid',
    })
    .transform((phone) => phone.replace('+', '')),
  message: z
    .string()
    .min(1, 'twilioSms.validation.messageRequired')
    .max(MAX_MESSAGE_LENGTH, 'twilioSms.validation.messageMaxLength'),
});

type TwilioSmsFormData = z.infer<typeof twilioSmsFormSchema>;

/**
 * TwilioSmsChannelDetail component displays SMS channel form for phone number input
 * Matches the design with centered layout, phone input, message field, and consent checkbox
 */
const TwilioSmsChannelDetail: React.FC<TwilioSmsChannelDetailProps> = ({
  onBack,
  onMinimize,
}) => {
  const scrollbar = useScrollbar();
  const { t } = useTranslation('common');
  const { currentChannelDetailType } = useWidgetUIStore();
  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState(false);
  // SMS Form Store
  const { formData, actions: smsFormActions } = useSmsFormStore();

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setError: setFormError,
  } = useForm<TwilioSmsFormData>({
    resolver: zodResolver(twilioSmsFormSchema),
    defaultValues: formData,
    mode: 'onSubmit',
  });

  const [error, setError] = useState('');

  const isPhoneNumberFilled =
    useWatch({ control, name: 'phoneNumber' })?.trim() !== '';
  const isMessageFilled = useWatch({ control, name: 'message' })?.trim() !== '';
  const isFilled = isPhoneNumberFilled && isMessageFilled;

  // Sync form changes with store
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name && value[name] !== undefined) {
        smsFormActions.updateField(
          name as keyof TwilioSmsFormData,
          value[name],
        );
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, smsFormActions]);

  const { channelData } = useChannels({
    channelType: currentChannelDetailType as keyof ChannelOptions,
  });

  const { mutate: submitSmsContactForm, isPending: isSubmitting } =
    useSmsContactForm();

  // Get the phone number from channel data for the "or" section
  const channelPhoneNumber = String(channelData?.phoneNumber || '');

  // Form submission handler
  const onSubmit = (data: TwilioSmsFormData) => {
    submitSmsContactForm(
      {
        phoneNumber: data.phoneNumber,
        message: data.message,
        isMarketingConsent: true,
      },
      {
        onSuccess: () => {
          reset();
          smsFormActions.clearForm(); // Clear persisted form data
          setIsSubmissionSuccessful(true);
        },
        onError: (error) => {
          setIsSubmissionSuccessful(false);
          if (
            error instanceof SmsContactFormError &&
            error.errorCode === SmsContactFormErrorCode.RateLimitExceeded
          ) {
            setFormError('phoneNumber', {
              message: 'errors.twilioSms.rateLimitExceeded',
            });
          } else {
            setError('errors.twilioSms.submissionError');
          }
        },
      },
    );
  };

  // Handle close from success page
  const handleClose = () => {
    setIsSubmissionSuccessful(false);
    // Optionally go back to the previous page
    onBack?.();
  };

  // Show success page if submission was successful
  if (isSubmissionSuccessful) {
    return <SmsSuccessPage onBack={onBack} onClose={handleClose} />;
  }

  return (
    <div
      className={classNames(
        'sf:w-full sf:h-full sf:max-w-md sf:mx-auto sf:overflow-hidden sf:background-gradient-diagonal sf:flex sf:flex-col',
        'sf:sm:rounded-2xl',
      )}
      style={scrollbar.scrollbarStyle}
      onMouseEnter={scrollbar.onMouseEnter}
      onMouseLeave={scrollbar.onMouseLeave}
    >
      <Header
        onBack={onBack}
        title={t('channels.twilioSms', 'SMS')}
        onMinimize={onMinimize}
      />

      {/* Main Content - Centered Layout */}
      <div
        className={classNames(
          'sf:flex-1 sf:flex sf:flex-col sf:items-center sf:justify-start sf:py-[56px] sf:px-[32px] sf:text-center sf:overflow-auto sf:overscroll-contain',
          'sf:sm:pt-[56px] sf:sm:pb-6 sf:sm:px-[50px]',
        )}
      >
        {/* SMS Icon */}
        <div className="sf:mb-6">
          <TwilioSmsIcon width="32" height="32" className="sf:mx-auto" />
        </div>

        {/* Title */}
        <h1 className="sf:text-headline2 sf:font-semibold sf:mb-2 sf:max-w-sm sf:leading-tight">
          {t('twilioSms.title', 'Chat with us via SMS')}
        </h1>

        {/* Description */}
        <p className="sf:text-body1 sf:text-[#3C3F44] sf:mb-4 sf:max-w-sm sf:leading-relaxed">
          {t(
            'twilioSms.description',
            'Enter your phone number and we will contact your via SMS shortly',
          )}
        </p>

        {/* SMS Form */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="sf:w-full sf:max-w-sm sf:space-y-2"
        >
          {/* Phone Number Input */}
          <div className="sf:w-full">
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  country={'hk'}
                  enableClickOutside={false}
                  value={field.value}
                  placeholder={t('contactFormMessage.phonePlaceholder')}
                  specialLabel=""
                  onChange={(phone) => field.onChange(phone)}
                  onBlur={field.onBlur}
                  inputProps={{
                    required: true,
                    autoComplete: 'tel',
                  }}
                  inputClass="sf:placeholder-gray-300"
                  inputStyle={{
                    width: '100%',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    borderColor: errors.phoneNumber
                      ? 'var(--sf-color-red-500)'
                      : 'var(--sf-color-gray-300)',
                    borderRadius: 'var(--sf-radius-sm)',
                    paddingTop: '6px',
                    paddingRight: 'calc(var(--sf-spacing) * 3)',
                    paddingBottom: '6px',
                    paddingLeft: 'calc(var(--sf-spacing) * 14)',
                    fontSize: 'var(--sf-text-base)',
                    lineHeight: 'var(--sf-text-base--line-height)',
                    height: '48px',
                    color: 'var(--sf-color-gray-900)',
                    backgroundColor: errors.phoneNumber
                      ? 'var(--sf-color-bg-error)'
                      : 'var(--sf-color-white)',
                  }}
                  buttonClass="sf:hover:bg-none"
                  buttonStyle={{
                    border: 'none',
                    borderRadius: 'var(--sf-radius-md) 0 0 var(--sf-radius-md)',
                  }}
                />
              )}
            />
            {errors.phoneNumber && (
              <p className="sf:text-red-500 sf:text-sm sf:mt-1 sf:text-left">
                {t(errors.phoneNumber.message || '')}
              </p>
            )}
          </div>

          {/* Message Input */}
          <div className="sf:w-full">
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  data-testid="message-input"
                  placeholder={t('contactFormMessage.messagePlaceholder')}
                  className={classNames({
                    'sf:w-full sf:min-h-[120px] sf:bg-white sf:p-3 sf:border sf:rounded-sm sf:text-base sf:text-gray-900 sf:placeholder-gray-300 sf:border-gray-300 sf:resize-none sf:focus:outline-none sf:focus:ring-2 sf:focus:ring-blue-500 sf:focus:border-transparent':
                      true,
                    'sf:border-red-500 sf:bg-bg-error': errors.message,
                  })}
                  maxLength={MAX_MESSAGE_LENGTH}
                  rows={4}
                />
              )}
            />
            <div className="sf:flex sf:justify-between sf:items-center sf:mt-1">
              {errors.message && (
                <p className="sf:text-red-500 sf:text-sm">
                  {t(
                    errors.message.message ||
                      'twilioSms.validation.messageRequired',
                    'Message is required',
                  )}
                </p>
              )}
              {error && <p className="sf:text-red-500 sf:text-sm">{error}</p>}
            </div>
          </div>

          {/* Marketing Consent */}
          <div className="sf:flex sf:items-start sf:text-left sf:space-x-3">
            <p className="sf:text-body2 sf:text-[#3C3F44] sf:leading-relaxed sf:select-none">
              {t('twilioSms.marketingConsentNew', {
                defaultValue:
                  'By submitting, you authorize us to text/call the number above, possibly using automated means and/or AI-generated calls/content. Message frequency varies. Consent is not a condition of purchase.',
              })}
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || !isFilled}
            className="sf:bg-blue-500 sf:text-white sf:w-[100px] sf:h-10 sf:px-6 sf:rounded-full sf:font-semibold sf:focus:outline-none sf:focus:ring-4 sf:focus:ring-blue-300 sf:focus:ring-opacity-50 sf:disabled:opacity-50 sf:disabled:cursor-not-allowed sf:disabled:transform-none sf:mt-5"
          >
            {isSubmitting ? (
              <div className="sf:w-4 sf:h-4 sf:border-2 sf:border-gray-300 sf:border-t-widget-button-color sf:rounded-full sf:animate-spin sf:mx-auto" />
            ) : (
              t('buttons.submit', 'Submit')
            )}
          </button>
        </form>

        {/* Or Section */}
        {channelPhoneNumber && (
          <>
            <div className="sf:w-full sf:flex sf:items-center sf:justify-center sf:gap-2 sf:my-6">
              <div className="sf:w-full sf:h-0.5 sf:bg-gray-200"></div>
              <p className="sf:text-gray-500 sf:text-sm sf:whitespace-nowrap">
                {t('channelDetail.common.or', 'or')}
              </p>
              <div className="sf:w-full sf:h-0.5 sf:bg-gray-200"></div>
            </div>

            <div className="sf:text-center">
              <p className="sf:text-body1 sf:mb-2">
                <Trans
                  i18nKey="twilioSms.directSmsDescription"
                  components={{
                    phoneNumber: (
                      <a
                        href={`sms:${channelPhoneNumber}`}
                        className="sf:text-blue-90 sf:no-underline"
                      >
                        {channelPhoneNumber}
                      </a>
                    ),
                  }}
                  values={{
                    phoneNumber: channelPhoneNumber,
                  }}
                ></Trans>
              </p>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TwilioSmsChannelDetail;
